import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMe } from "../../hooks/useMe";
import { runFromSearch } from "../../lib/bobReports";
import { findingFromSearch, prFromSearch } from "../../lib/ledgerFormat";
import { parseRoute } from "../../lib/route";

export const REPORT_TABS = ["ledger", "overview", "reports", "queue"] as const;
export type ReportTab = (typeof REPORT_TABS)[number];

/** What a report can link to. Links into this project switch tabs in place; another pack's are plain hrefs. */
export interface ReportNav {
  project: string;
  projectName: string;
  isAdmin: boolean;
  /** The run `?run=` names, or null for the runs list. */
  runId: number | null;
  openRun: (id: number | null) => void;
  openFinding: (id: number) => void;
  /** Every tab change goes through here, so the address and the open run follow it. */
  openTab: (tab: ReportTab) => void;
  findingHref: (id: number, project?: string) => string;
  runHref: (id: number) => string;
  packHref: (project: string) => string;
}

/** What the console needs to link a run to its report. */
export type RunNav = Pick<ReportNav, "runHref" | "openRun">;

const packPath = (project: string) => `/review/${encodeURIComponent(project)}`;

// The tab rides in the history entry's state, so Back and Forward land on the
// tab each entry was made on even when its address carries no query.
interface NavState { bobTab?: unknown }

function isTab(v: unknown): v is ReportTab {
  return typeof v === "string" && (REPORT_TABS as readonly string[]).includes(v);
}

function writeEntry(project: string, search: string, tab: ReportTab, push: boolean): void {
  if (typeof window === "undefined") return;
  const path = window.location.pathname.startsWith(packPath(project)) ? window.location.pathname : packPath(project);
  const state: NavState = { bobTab: tab };
  try {
    if (push) window.history.pushState(state, "", `${path}${search}`);
    else window.history.replaceState(state, "", `${path}${search}`);
  } catch { /* environments that forbid history changes keep the in-page state */ }
}

/** The tab and run the current history entry describes. A query wins over the saved tab. */
export function locationReport(): { tab: ReportTab; runId: number | null } {
  if (typeof window === "undefined") return { tab: "ledger", runId: null };
  const { search } = window.location;
  const runId = runFromSearch(search);
  if (runId !== null) return { tab: "reports", runId };
  if (findingFromSearch(search) !== null || prFromSearch(search) !== null) return { tab: "ledger", runId: null };
  const saved = (window.history.state as NavState | null)?.bobTab;
  return { tab: isTab(saved) ? saved : "ledger", runId: null };
}

function urlNamesProject(project: string): boolean {
  return typeof window !== "undefined" && parseRoute(window.location.pathname).projectId === project;
}

export function useReportNav(project: string, projectName: string | undefined, onTab: (tab: ReportTab) => void): ReportNav {
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const [runId, setRunId] = useState<number | null>(() => locationReport().runId);

  const follow = useCallback(() => {
    const at = locationReport();
    setRunId(at.runId);
    onTab(at.tab);
  }, [onTab]);

  // A run id belongs to one pack, so a project switch drops it, unless the
  // address itself moved to that pack (Back, Forward) and says where to land.
  const shownProject = useRef(project);
  useEffect(() => {
    if (shownProject.current === project) return;
    shownProject.current = project;
    if (urlNamesProject(project)) follow();
    else setRunId(null);
  }, [project, follow]);

  useEffect(() => {
    const onPop = () => { if (urlNamesProject(project)) follow(); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [project, follow]);

  const openRun = useCallback((id: number | null) => {
    writeEntry(project, id === null ? "" : `?run=${id}`, "reports", true);
    setRunId(id);
    onTab("reports");
  }, [project, onTab]);

  const openFinding = useCallback((id: number) => {
    writeEntry(project, `?finding=${id}`, "ledger", true);
    setRunId(null);
    onTab("ledger");
  }, [project, onTab]);

  const openTab = useCallback((tab: ReportTab) => {
    // The console keeps its own `?finding=` and `?pr=`; every other tab, and a run, leave the address bare.
    const search = typeof window === "undefined" ? "" : window.location.search;
    const keep = tab === "ledger" && runFromSearch(search) === null ? search : "";
    writeEntry(project, keep, tab, false);
    setRunId(null);
    onTab(tab);
  }, [project, onTab]);

  return useMemo(() => ({
    project,
    projectName: projectName ?? project,
    isAdmin,
    runId,
    openRun,
    openFinding,
    openTab,
    findingHref: (id: number, p = project) => `${packPath(p)}?finding=${id}`,
    runHref: (id: number) => `${packPath(project)}?run=${id}`,
    packHref: packPath,
  }), [project, projectName, isAdmin, runId, openRun, openFinding, openTab]);
}
